import type {
  MerchantMovementKind,
  MerchantOrderItem,
  MerchantOrdersQuery,
  MerchantOrderSide,
  MerchantOrderStatus,
  MerchantTimelineKind,
} from "../api";

export interface MerchantOrderFilterValues {
  remoteId: string;
  status: "" | MerchantOrderStatus;
  side: "" | MerchantOrderSide;
  createdFrom: string;
  createdTo: string;
  minAmount: string;
  maxAmount: string;
}

export const EMPTY_MERCHANT_ORDER_FILTERS: MerchantOrderFilterValues = {
  remoteId: "",
  status: "",
  side: "",
  createdFrom: "",
  createdTo: "",
  minAmount: "",
  maxAmount: "",
};

const ORDER_STATUS_LABELS: Record<MerchantOrderStatus, string> = {
  NEW: "Новая",
  PROCESS: "В процессе",
  APPEAL: "Спор",
  CANCEL: "Отменена",
  OK: "Выполнена",
};

const TIMELINE_LABELS: Record<string, string> = {
  created: "Заявка создана",
  published: "Заявка опубликована",
  processing_started: "Начата обработка",
  requisites_ready: "Реквизиты готовы",
  // Чек бывает и доказательством отмены: «подтверждение оплаты» у отменённой заявки — неправда.
  receipt_uploaded: "Загружен чек",
  part_payment_failed: "Получено доказательство неудачной оплаты части",
  part_payment_confirmed: "Трейдер подтвердил поступление по части",
  part_requisites_replaced: "Выдан новый реквизит для неоплаченной части",
  partially_completed: "Часть заявки выполнена",
  returned_to_pool: "Заявка снова доступна для обработки",
  appeal: "Открыт спор",
  cancelled: "Обработка отменена",
  completed: "Заявка выполнена",
};

const MOVEMENT_LABELS: Record<string, string> = {
  payment: "Расчёт по заявке",
  balance_reset: "Сверка баланса",
  manual_topup: "Пополнение",
  manual_withdrawal: "Вывод",
  merchant_withdrawal_reserve: "Резерв под вывод USDT",
  merchant_withdrawal_release: "Возврат резерва вывода USDT",
  blockchain_deposit: "Пополнение USDT TRC20",
  historical_correction: "Историческая корректировка",
};

const MERCHANT_STATUSES: readonly MerchantOrderStatus[] = [
  "NEW",
  "PROCESS",
  "APPEAL",
  "CANCEL",
  "OK",
];

function isMerchantStatus(value: string): value is MerchantOrderStatus {
  return MERCHANT_STATUSES.some((status) => status === value);
}

function isMerchantSide(value: string): value is MerchantOrderSide {
  return value === "in" || value === "out";
}

export function merchantStatusLabel(status: MerchantOrderStatus): string {
  return ORDER_STATUS_LABELS[status];
}

export function merchantOrderStatusLabel(item: MerchantOrderItem): string {
  const base = merchantStatusLabel(item.status);
  return item.status === "PROCESS" && item.successfulParts > 0
    ? `${base} · частично выполнено`
    : base;
}

// Знаменатель `maxParts` — это потолок дробления заявки, а не план работ, поэтому
// дробь «1 / 4» у закрытой заявки читается как незавершённость. Показываем лимит
// только там, где он ещё может сработать: заявка не в терминальном статусе и
// дробить действительно разрешено больше одного раза. `maxParts = 0` —
// заявка внешнего источника без лимита дробления.
const TERMINAL_MERCHANT_STATUSES: readonly MerchantOrderStatus[] = ["OK", "CANCEL"];

export function merchantOrderPartsLabel(item: MerchantOrderItem): string {
  const done = String(item.successfulParts);
  if (item.maxParts <= 1) return done;
  if (TERMINAL_MERCHANT_STATUSES.some((status) => status === item.status)) return done;
  return `${done} из ${item.maxParts}`;
}

export function merchantStatusTone(status: MerchantOrderStatus): string {
  switch (status) {
    case "OK":
      return "success";
    case "PROCESS":
      return "pending";
    case "APPEAL":
      return "appeal";
    case "CANCEL":
      return "failed";
    case "NEW":
      return "new";
  }
}

export function merchantTimelineLabel(kind: MerchantTimelineKind): string {
  return TIMELINE_LABELS[kind] ?? "Обновление заявки";
}

export function merchantMovementLabel(kind: MerchantMovementKind): string {
  return MOVEMENT_LABELS[kind] ?? "Операция по балансу";
}

export function merchantDirectionLabel(side: MerchantOrderSide): string {
  return side === "in" ? "Входящая" : "Исходящая";
}

export function formatMerchantDate(
  value: string | Date | null | undefined,
): string {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  const timestamp = date.getTime();
  if (timestamp !== timestamp) return typeof value === "string" ? value : "—";
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function parseMerchantOrderFilters(
  route: string,
): MerchantOrderFilterValues {
  const queryStart = route.indexOf("?");
  if (queryStart === -1) return {...EMPTY_MERCHANT_ORDER_FILTERS};
  const params = new URLSearchParams(route.slice(queryStart + 1));
  const status = params.get("status") ?? "";
  const side = params.get("side") ?? "";
  return {
    remoteId: params.get("remoteId") ?? "",
    status: isMerchantStatus(status) ? status : "",
    side: isMerchantSide(side) ? side : "",
    createdFrom: params.get("createdFrom") ?? "",
    createdTo: params.get("createdTo") ?? "",
    minAmount: params.get("minAmount") ?? "",
    maxAmount: params.get("maxAmount") ?? "",
  };
}

function setFilter(
  params: URLSearchParams,
  name: keyof MerchantOrderFilterValues,
  value: string,
): void {
  const normalized = value.trim();
  if (normalized) params.set(name, normalized);
}

export function merchantOrdersHash(
  filters: MerchantOrderFilterValues,
): string {
  const params = new URLSearchParams();
  setFilter(params, "remoteId", filters.remoteId);
  setFilter(params, "status", filters.status);
  setFilter(params, "side", filters.side);
  setFilter(params, "createdFrom", filters.createdFrom);
  setFilter(params, "createdTo", filters.createdTo);
  setFilter(params, "minAmount", filters.minAmount);
  setFilter(params, "maxAmount", filters.maxAmount);
  const query = params.toString();
  return `#/merchant/orders${query ? `?${query}` : ""}`;
}

export function merchantOrdersHashForRemoteId(remoteId: string): string {
  return merchantOrdersHash({
    ...EMPTY_MERCHANT_ORDER_FILTERS,
    remoteId,
  });
}

function localDateBoundary(
  value: string,
  boundary: "start" | "end",
): string | undefined {
  if (!value) return undefined;
  const time = boundary === "start" ? "00:00:00.000" : "23:59:59.999";
  const date = new Date(`${value}T${time}`);
  const timestamp = date.getTime();
  if (timestamp !== timestamp) return undefined;
  return date.toISOString();
}

export function merchantFiltersToQuery(
  filters: MerchantOrderFilterValues,
): MerchantOrdersQuery {
  return {
    remoteId: filters.remoteId.trim() || undefined,
    status: filters.status || undefined,
    side: filters.side || undefined,
    createdFrom: localDateBoundary(filters.createdFrom, "start"),
    createdTo: localDateBoundary(filters.createdTo, "end"),
    minAmount: filters.minAmount.trim() || undefined,
    maxAmount: filters.maxAmount.trim() || undefined,
  };
}

export function hasMerchantOrderFilters(
  filters: MerchantOrderFilterValues,
): boolean {
  return Object.values(filters).some((value) => value.trim() !== "");
}
